import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  QueryList,
  ViewChildren,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
  ReactiveFormsModule,
  FormArray,
  FormControl,
} from '@angular/forms';
import {
  IonInput,
  IonGrid,
  IonCol,
  IonRow,
  IonReorderGroup,
  IonReorder,
} from '@ionic/angular/standalone';
import { ItemReorderEventDetail } from '@ionic/core';
import {
  animationFrameScheduler,
  EMPTY,
  firstValueFrom,
  from,
  Observable,
  ReplaySubject,
} from 'rxjs';
import {
  delay,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
} from 'rxjs/operators';
import { AlfredClientService } from '../../../../alfred-client.service';
import { ListQuestionAnswer } from '../../../../models/answer/list-answer.model';
import { ListQuestion } from '../../../../models/questions/list-question.model';
import { OnLoaded } from '@lyceo/shared/dynamic-component-loading/feature-main';
import { ListType } from '../../../../shared/enums/list-type.enum';
import { QuestionControlType } from '../question-type.interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { QuestionDisplayComponent } from '../../question-display/question-display.component';

/**
 * List question allows multiple answers (number predetermined),
 * Answers are returned as a list of strings.
 *
 * @example
 * <lyceo-lib-list-question
 *                               [question]="questionData.question"
 *                               [formControl]="questionFormCtrl">
 * </lyceo-lib-list-question>
 */
@Component({
  selector: 'lyceo-lib-list-question',
  templateUrl: './list-question.component.html',
  styleUrls: ['./list-question.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    QuestionDisplayComponent,
    IonGrid,
    IonCol,
    IonRow,
    IonReorderGroup,
    IonInput,
    ReactiveFormsModule,
    IonReorder,
  ],
})
export class ListQuestionComponent
  implements AfterViewInit, OnLoaded, QuestionControlType
{
  public alfredClientService: AlfredClientService = inject(AlfredClientService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  /**
   * Expose ListType enum to template.
   */
  public readonly ListType: typeof ListType = ListType;

  /**
   * List of inputs to apply the autofocus
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @ViewChildren(IonInput, { read: ElementRef })
  public inputs!: QueryList<IonInput>;

  /**
   * Array of characters so we can map the index of a choice to a character.
   */
  public readonly Alfabet: string[] = 'abcdefghijklmnopqrstuvwxyz'.split('');

  /**
   * The form control for answers list
   */
  public answers: FormArray<FormControl> = new FormArray<FormControl>(
    [],
    [Validators.required, this.checkLength()],
  );

  /**
   * Set the ListQuestion that we are answering.
   */
  public set question(question: ListQuestion) {
    this._question = question;

    // Initialize the answer by creating `numberOfAnswer` fields with NULL as value.
    this.answer = Array(question.numberOfAnswers).fill(null);

    // While rebuilding the form array we don't want to emit change events.
    // Therefore we set the 'resizing' guard to TRUE upon start, and finally back
    // to FALSE.
    this.resizing = true;
    this.answers.clear();
    for (let i: number = 0; i < question.numberOfAnswers; i++) {
      this.answers.push(
        new FormControl(null, [Validators.required, Validators.maxLength(255)]),
      );
    }
    this.resizing = false;
  }

  /**
   * Get the ListQuestion that we are answering.
   */
  public get question(): ListQuestion {
    return this._question;
  }

  /**
   * questions data used to create the list question.
   */
  public _question!: ListQuestion;

  /**
   * Input to prefill the answer of the question.
   */
  public set answer(answer: ListQuestionAnswer) {
    if (!answer || answer.length !== this.question.numberOfAnswers) {
      this.answers.reset();
      return;
    }

    this.answers.patchValue(answer);
    this.answers.markAsPristine();
  }

  /**
   * The current answer of the question.
   */
  public get answer(): ListQuestionAnswer {
    return this.getValue();
  }

  /**
   * Subject which starts to emit when the view is rendered. This is needed because the
   * component is dynamically loading.
   */
  public onFinishedInit$: ReplaySubject<void> = new ReplaySubject<void>();

  /**
   * Whether we are currently resizing the form array.
   */
  private resizing: boolean = false;

  /**
   * @inheritDoc
   */
  public ngAfterViewInit(): void {
    // Prevent stealing focus from the Lyceo Alfred Dashboard. Otherwise
    // the input gets focussed each key press in the question builder. Making it
    // near impossible to define the question.
    if (!this.alfredClientService.isDirectHandleMode()) {
      this.focus();
    }

    this.onFinishedInit$.next();
    this.onFinishedInit$.complete();
  }

  /**
   * @inheritDoc
   */
  public ready(): Promise<void> {
    return firstValueFrom(this.onFinishedInit$.pipe(delay(20)));
  }

  /**
   * Focus the first input element.
   */
  public focus(): void {
    this.inputs.changes
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(this.inputs),
        filter((inputs) => inputs.length !== 0),
        switchMap((inputs) => {
          const firstElement: HTMLIonInputElement = inputs.first.nativeElement;

          // Unfortunately this function doesn't exist in tests.
          if ('getInputElement' in firstElement) {
            return from(firstElement.getInputElement());
          }

          // In tests we don't emit an input field.
          return EMPTY;
        }),
        delay(0, animationFrameScheduler),
      )
      .subscribe((input) => {
        input.focus();
      });
  }

  /**
   * @inheritDoc
   */
  public isPristine(): boolean {
    return this.answers.pristine;
  }

  /**
   * @inheritDoc
   */
  public getValue(): ListQuestionAnswer {
    return this.answers.valid ? this.answers.value : [];
  }

  /**
   * @inheritDoc
   */
  public onValueChanges(): Observable<ListQuestionAnswer> {
    return this.answers.valueChanges.pipe(
      startWith(this.answers.value),
      filter(() => !this.resizing),
      map((value) => (this.isValid() ? value : [])),
      distinctUntilChanged((oldValue, newValue) => {
        return (
          oldValue.length === newValue.length &&
          oldValue.every((value, index) => value === newValue[index])
        );
      }),
    );
  }

  /**
   * @inheritDoc
   */
  public isValid(): boolean {
    return this.answers.valid;
  }

  /**
   * @inheritDoc
   */
  public onValidChanges(): Observable<boolean> {
    return this.answers.statusChanges.pipe(
      filter(() => !this.resizing),
      filter((status) => status === 'VALID' || status === 'INVALID'),
      map((status) => status === 'VALID'),
    );
  }

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.answers.disable();
    } else {
      this.answers.enable();
    }
  }

  /**
   * Orders the form control array based on user interactions
   * in the view (via ion-reorder)
   * @param event moved answer
   */
  public reorderItems(event: CustomEvent<ItemReorderEventDetail>): void {
    const value: string[] = this.answers.value;
    const newValue: AbstractControl[] = event.detail.complete(value);
    this.answers.patchValue(newValue);
  }

  /**
   * Check whether the user gave exactly the correct amount of answers.
   */
  private checkLength():
    | ValidatorFn
    | ((control: AbstractControl) => ValidationErrors | null) {
    return (control): ValidationErrors | null => {
      if (control.value.length === this.question?.numberOfAnswers) {
        return null;
      }

      return { lengthNotCorrect: true };
    };
  }
}
